import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { OUTREACH_POPUP } from "../../../theme/tokens";
import { OutreachStatus } from "../../../utils/models";
import MetaChip, { type MetaChipTone } from "../../UIToolkit/Badges/MetaChip";
import { ColorDot } from "../../UIToolkit/molecules/ColorDot";

const TONE: Record<OutreachStatus, { tone: MetaChipTone; dot: string }> = {
	[OutreachStatus.Running]: { tone: "success", dot: "success.main" },
	[OutreachStatus.Paused]: { tone: "warning", dot: "warning.main" },
	[OutreachStatus.Filled]: { tone: "primary", dot: "primary.main" },
	[OutreachStatus.Finished]: { tone: "neutral", dot: "text.secondary" },
	[OutreachStatus.Canceled]: { tone: "error", dot: "error.main" },
};

// The running dot breathes so "still asking" reads as live; nothing moves under reduced motion.
const PULSE_SX = {
	"@keyframes outreachStatusPulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: OUTREACH_POPUP.STATUS_PULSE_MIN_OPACITY } },
	animation: `outreachStatusPulse ${OUTREACH_POPUP.STATUS_PULSE_MS}ms ease-in-out infinite`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
} as const;

/** Where outreach stands — running, paused, filled, finished or canceled — as one chip beside the popup title. */
export const OutreachStatusChip: FC<{ status: OutreachStatus }> = ({ status }) => {
	const { t } = useTranslation();
	const { tone, dot } = TONE[status];
	return (
		<span role="status">
			<MetaChip
				size="medium"
				tone={tone}
				label={t(`request.outreach.status.${status}`)}
				icon={<ColorDot color={dot} sx={status === OutreachStatus.Running ? PULSE_SX : undefined} />}
				dataTestId="outreach-status"
			/>
		</span>
	);
};
